import { useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { ArrowRight, Loader2, Check, AlertCircle, Shield, Clock, Euro } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { isDark } = useTheme();
  
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Entre une adresse email valide');
      return;
    }
    setStatus('loading');
    try {
      const response = await axios.post(`${API}/beta-subscribe`, { email });
      setStatus('success');
      setMessage(response.data.message);
      setEmail('');
    } catch (error) {
      setStatus('error');
      setMessage('Une erreur est survenue');
    }
  };

  return (
    <section 
      id="cta"
      className={`section-apple px-6 ${isDark ? 'bg-black' : 'bg-[#f5f5f7]'}`}
      data-testid="cta-section"
    >
      <div className="max-w-[680px] mx-auto text-center">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          {/* Main headline - Complete promise */}
          <h2 className={`text-[36px] sm:text-[44px] md:text-[52px] font-semibold tracking-[-0.03em] leading-[1.1] mb-4 ${
            isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
          }`}>
            Estime ce que
            <br />
            tu peux <span className="text-[#81d8d0]">récupérer.</span>
            <br />
            <span className={isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'}>Reprends le contrôle.</span>
          </h2>
          
          <p className={`text-[17px] sm:text-[19px] tracking-[-0.016em] mb-8 max-w-[480px] mx-auto ${
            isDark ? 'text-[#86868b]' : 'text-[#6e6e73]'
          }`}>
            Inscris-toi à la bêta : tu reçois un mail dès que ton accès est prêt.
          </p>

          {/* Email form - Apple minimal */}
          <form onSubmit={handleSubmit} className="space-y-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                placeholder="ton@email.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
                disabled={status === 'loading'}
                data-testid="email-input"
                className={`flex-1 h-14 px-5 rounded-xl text-[17px] tracking-[-0.02em] border outline-none transition-colors ${
                  isDark 
                    ? 'bg-[#141414] border-[#3a3a3c] text-[#f5f5f7] placeholder-[#6e6e73] focus:border-[#81d8d0]' 
                    : 'bg-white border-[#d2d2d7] text-[#1d1d1f] placeholder-[#86868b] focus:border-[#81d8d0]'
                }`}
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                data-testid="submit-button"
                className="btn-apple btn-apple-primary h-14 px-8 whitespace-nowrap disabled:opacity-60 text-[17px]"
              >
                {status === 'loading' ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <>
                    Réserver ma place
                    <ArrowRight size={18} strokeWidth={1.5} className="ml-2" />
                  </>
                )}
              </button>
            </div>
            
            {/* Status message */}
            {status !== 'idle' && status !== 'loading' && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex items-center justify-center gap-2 text-[15px] ${
                  status === 'success' ? 'text-[#81d8d0]' : 'text-[#FF6B6B]'
                }`}
              >
                {status === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
                {message}
              </motion.p>
            )}
          </form>

          {/* Micro-reassurance badges */}
          <div className={`flex flex-wrap items-center justify-center gap-6 text-[13px] ${isDark ? 'text-[#6e6e73]' : 'text-[#86868b]'}`}>
            <span className="flex items-center gap-1.5">
              <Euro size={14} strokeWidth={1.5} className="text-[#81d8d0]" />
              100% gratuit
            </span>
            <span className="flex items-center gap-1.5">
              <Shield size={14} strokeWidth={1.5} className="text-[#81d8d0]" />
              Données chiffrées
            </span>
            <span className="flex items-center gap-1.5">
              <Clock size={14} strokeWidth={1.5} className="text-[#81d8d0]" />
              Hébergé en France
            </span>
          </div>

          <p className={`text-[12px] mt-4 ${isDark ? 'text-[#48484a]' : 'text-[#aeaeb2]'}`}>
            Pas de spam, juste un accès prioritaire quand on lance.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;
